import { osnovaApi } from '../base';
import type {
  HistoryEntry,
  ImportResult,
  ObjectPatch,
  Receipt,
  ReceiptFilter,
  ReceiptInput,
  ReceiptObjectsResponse,
  ReceiptPage,
  ReceiptSummary,
  ReceiptsPreview,
} from '@entities/mto/receipts';

/**
 * Журнал «Приход ТМЦ» — `/agents/mto/receipts*`. Читают МТО и Экономист,
 * ведёт МТО (`mto.receipts.write`), объекты сопоставляет экономист
 * (`mto.receipts.map`).
 */
export const mtoReceiptsApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    listMtoReceipts: b.query<ReceiptPage, ReceiptFilter>({
      query: (params) => ({ url: '/agents/mto/receipts', params }),
      providesTags: ['MtoReceipts'],
    }),
    getMtoReceiptSummary: b.query<ReceiptSummary, { year?: number } | void>({
      query: (p) => ({ url: '/agents/mto/receipts/summary', params: p ?? undefined }),
      providesTags: ['MtoReceipts'],
    }),
    listMtoReceiptObjects: b.query<ReceiptObjectsResponse, { year?: number } | void>({
      query: (p) => ({ url: '/agents/mto/receipts/objects', params: p ?? undefined }),
      providesTags: ['MtoReceipts'],
    }),
    createMtoReceipt: b.mutation<{ receipt: Receipt }, ReceiptInput>({
      query: (body) => ({ url: '/agents/mto/receipts', method: 'POST', body }),
      invalidatesTags: ['MtoReceipts'],
    }),
    updateMtoReceipt: b.mutation<{ receipt: Receipt }, { id: string; body: ReceiptInput }>({
      query: ({ id, body }) => ({ url: `/agents/mto/receipts/${id}`, method: 'PUT', body }),
      invalidatesTags: ['MtoReceipts'],
    }),
    deleteMtoReceipt: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/mto/receipts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MtoReceipts'],
    }),
    getMtoReceiptHistory: b.query<{ items: HistoryEntry[] }, string>({
      query: (id) => `/agents/mto/receipts/${id}/history`,
      providesTags: ['MtoReceipts'],
    }),
    updateMtoReceiptObject: b.mutation<{ ok: boolean }, { id: string; body: ObjectPatch }>({
      query: ({ id, body }) => ({
        url: `/agents/mto/receipts/objects/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['MtoReceipts'],
    }),
    /** Подсказать сопоставление заново для несопоставленных написаний. */
    resuggestMtoReceiptObjects: b.mutation<{ changed: number }, void>({
      query: () => ({ url: '/agents/mto/receipts/objects/resuggest', method: 'POST' }),
      invalidatesTags: ['MtoReceipts'],
    }),
    getMtoReceiptObjectHistory: b.query<{ items: HistoryEntry[] }, string>({
      query: (id) => `/agents/mto/receipts/objects/${id}/history`,
      providesTags: ['MtoReceipts'],
    }),
    /** Разбор книги: multipart с полем `file`. Ничего не пишет. */
    previewMtoReceiptsImport: b.mutation<{ preview: ReceiptsPreview }, FormData>({
      query: (form) => ({ url: '/agents/mto/receipts/import/preview', method: 'POST', body: form }),
    }),
    /** Запись книги: тот же `file`, `sha256` из разбора, `sheets` — листы
     *  по строке (пусто — все). */
    applyMtoReceiptsImport: b.mutation<{ result: ImportResult }, FormData>({
      query: (form) => ({ url: '/agents/mto/receipts/import/apply', method: 'POST', body: form }),
      invalidatesTags: ['MtoReceipts'],
    }),
  }),
});

export const {
  useListMtoReceiptsQuery,
  useGetMtoReceiptSummaryQuery,
  useListMtoReceiptObjectsQuery,
  useCreateMtoReceiptMutation,
  useUpdateMtoReceiptMutation,
  useDeleteMtoReceiptMutation,
  useGetMtoReceiptHistoryQuery,
  useUpdateMtoReceiptObjectMutation,
  useGetMtoReceiptObjectHistoryQuery,
  useResuggestMtoReceiptObjectsMutation,
  usePreviewMtoReceiptsImportMutation,
  useApplyMtoReceiptsImportMutation,
} = mtoReceiptsApi;
